import { ReactNode, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Colors, Icon } from "swing-components";

import { ButtonScore, useModal } from "~components";
import { calcCancelPenaltyType, RequestData, translate } from "~utils";

import { ModalCancelPolicy } from "../ModalCancelPolicy";

import "./ModalReconfirm.css";

import { useLocation } from "react-router-dom";

import { canSubReconfirm } from "./utils";

type ModalReconfirmProps = {
  children: ReactNode;
  onDismiss: (data?: string | null | undefined | number, role?: string) => void;
  onConfirm: (payload: "RECONFIRM" | "CANCEL") => void;
  title: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
};

export function ModalReconfirm(props: ModalReconfirmProps) {
  const {
    children,
    onDismiss,
    onConfirm,
    title,
    primaryButtonText = translate("MODAL_RECONFIRM_PRIMARY_BUTTON_TEXT"),
    secondaryButtonText = translate("MODAL_RECONFIRM_SECONDARY_BUTTON_TEXT"),
  } = props;

  return (
    <div className="modal-wrapper">
      <div className="modal-header">
        <h2>{title}</h2>
        <button onClick={() => onDismiss()} css={{ backgroundColor: "transparent" }}>
          <Icon name="Close" color={Colors.blue500} />
        </button>
      </div>
      <div className="modal-body">{children}</div>
      <div className="modal-footer">
        <div className="modal-footer-button-spacing">
          <ButtonScore label={primaryButtonText} onClick={() => onConfirm("RECONFIRM")} />
        </div>
        <ButtonScore
          label={secondaryButtonText}
          fill="outline"
          onClick={() => onConfirm("CANCEL")}
        />
      </div>
    </div>
  );
}

type ModalReconfirmContentProps = {
  displayDate: RequestData["displayDate"];
  isMultiDay: RequestData["isMultiDay"];
  schoolName: RequestData["school"]["name"];
  startDate: RequestData["startDate"];
};

export function ModalReconfirmContent(props: ModalReconfirmContentProps) {
  const { displayDate, isMultiDay, schoolName, startDate } = props;
  const penaltyType = calcCancelPenaltyType({
    reqStartTime: startDate,
    // Set to >30 to bypass grace period calculation
    reqFillTime: DateTime.now().setZone("UTC").minus({ minutes: 31 }),
    reqCancelTime: DateTime.now().setZone("UTC"),
  });
  // A true value signals the request begins within 24hours.
  const insideCancelWindow = penaltyType !== "NO_PENALTY";
  const { openModal: openCancelPolicyModal, closeModal: closeCancelPolicyModal } = useModal({
    component: <ModalCancelPolicy onDismiss={() => closeCancelPolicyModal()} />,
    modalOptions: {
      cssClass: `cancel-policy-modal`,
    },
  });

  return (
    <div>
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "24px 1fr",
          alignItems: "flex-start",
          gap: "16px",
        }}
      >
        <Icon name="School" />
        <p>{schoolName}</p>
        <Icon name={`${isMultiDay ? "Calendar Overlap" : "Calendar"}`} />
        <p>{displayDate}</p>
        <Icon name="Block" />
        <div>
          {insideCancelWindow
            ? translate("CANCEL_POLICY_WARNING_PENALTY")
            : translate("CANCEL_POLICY_WARNING_NO_PENALTY")}
          <span
            onClick={openCancelPolicyModal}
            css={{ color: Colors.blue500, textDecoration: "underline", cursor: "pointer" }}
          >
            {translate("CANCEL_POLICY_LABEL")}.
          </span>
        </div>
      </div>
    </div>
  );
}

export function useLocationQuery() {
  const { search } = useLocation();
  return useMemo(
    () => ({
      search: new URLSearchParams(search),
    }),
    [search],
  );
}

export function useModalReconfirmModal({
  request,
  onConfirm,
  onDismiss,
}: {
  request: RequestData;
  onConfirm: (payload: "RECONFIRM" | "CANCEL") => void;
  onDismiss: () => void;
}) {
  const locationQuery = useLocationQuery();
  const reconfirm = Boolean(locationQuery.search.get("reconfirm"));
  const [didModalDisplay, setDidModalDisplay] = useState(false);
  const shouldShowReconfirmModal = canSubReconfirm(request, reconfirm) && !didModalDisplay;
  const { openModal, closeModal } = useModal({
    component: (
      <ModalReconfirm
        title={translate("MODAL_RECONFIRM_TITLE")}
        onDismiss={() => {
          onDismiss();
          setDidModalDisplay(true);
        }}
        onConfirm={async (payload: "RECONFIRM" | "CANCEL") => {
          onConfirm(payload);
          setDidModalDisplay(true);
        }}
      >
        <ModalReconfirmContent
          displayDate={request.displayDate}
          isMultiDay={request.isMultiDay}
          schoolName={request.school.name}
          startDate={request.startDate}
        />
      </ModalReconfirm>
    ),
    modalOptions: {
      cssClass: "modal-reconfirm",
    },
  });

  return {
    openReconfirmModal: openModal,
    closeReconfirmModal: closeModal,
    reconfirm,
    shouldShowReconfirmModal,
  };
}
